import { createTheme } from "@mui/material/styles";
import PlayFontBold from "../assets/font/Play-Bold.ttf";
import PlayFontRegular from "../assets/font/Play-Regular.ttf";
import { elGR } from "@mui/material/locale";

import { colors } from "./colors";
import { fonts } from "./fonts";

const { Primary, Error, Warning, Success, Grey, Base } = colors;
const { display, text } = fonts;

export const playerDexTheme = createTheme(
  {
    typography: {
      fontFamily: [
        "Play",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(","),
    },
    palette: {
      // Use your scales for MUI's standard color slots
      primary: {
        main: Primary[600],
        light: Primary[500],
        dark: Primary[700],
        contrastText: Base.White,
      },
      secondary: {
        main: Grey[600],
        light: Grey[500],
        dark: Grey[700],
        contrastText: Base.White,
      },
      error: {
        main: Error[600],
        light: Error[500],
        dark: Error[700],
        contrastText: Base.White,
      },
      warning: {
        main: Warning[600],
        light: Warning[500],
        dark: Warning[700],
        contrastText: Base.White,
      },
      success: {
        main: Success[600],
        light: Success[500],
        dark: Success[700],
        contrastText: Base.White,
      },

      // keep your custom entries if you use them elsewhere
      grey: {
        // MUI already has `palette.grey`; but you also use it semantically.
        // Preserve your tone scale by exposing `main` and retaining others.
        main: Grey[500],
        50: Grey[50],
        100: Grey[100],
        200: Grey[200],
        300: Grey[300],
        400: Grey[400],
        500: Grey[500],
        600: Grey[600],
        700: Grey[700],
        800: Grey[800],
        900: Grey[900],
      },
      // if you use it for backgrounds etc.
      background: {
        default: Base.OffWhite,
        paper: Base.White,
      },
    },
    components: {
      MuiTableRow: {
        styleOverrides: {
          root: { transition: "all 0.3s ease-out" },
        },
      },

      // ✨ Buttons use only your palette colors (primary/secondary/error/warning/success)
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
            padding: "10px 16px",
            ...text.sm.normal,
          },

          // ------- CONTAINED -------
          containedPrimary: {
            backgroundColor: Primary[600],
            border: `1px solid ${Primary[600]}`,
            color: Base.White,
            "&:hover": {
              backgroundColor: Primary[700],
              borderColor: Primary[700],
            },
            "&:focus": { boxShadow: "none", backgroundColor: Primary[600] },
            "&.Mui-disabled": {
              backgroundColor: Primary[200],
              borderColor: Primary[200],
            },
          },
          containedSecondary: {
            backgroundColor: Grey[600],
            border: `1px solid ${Grey[600]}`,
            color: Base.White,
            "&:hover": { backgroundColor: Grey[700], borderColor: Grey[700] },
            "&.Mui-disabled": {
              backgroundColor: Grey[300],
              borderColor: Grey[300],
            },
          },
          containedError: {
            backgroundColor: Error[600],
            border: `1px solid ${Error[600]}`,
            color: Base.White,
            "&:hover": { backgroundColor: Error[700], borderColor: Error[700] },
            "&.Mui-disabled": {
              backgroundColor: Error[500],
              borderColor: Error[500],
            },
          },
          containedWarning: {
            backgroundColor: Warning[600],
            border: `1px solid ${Warning[600]}`,
            color: Base.White,
            "&:hover": {
              backgroundColor: Warning[700],
              borderColor: Warning[700],
            },
            "&.Mui-disabled": {
              backgroundColor: Warning[500],
              borderColor: Warning[500],
            },
          },
          containedSuccess: {
            backgroundColor: Success[600],
            border: `1px solid ${Success[600]}`,
            color: Base.White,
            "&:hover": {
              backgroundColor: Success[700],
              borderColor: Success[700],
            },
            "&.Mui-disabled": {
              backgroundColor: Success[500],
              borderColor: Success[500],
            },
          },

          // ------- OUTLINED -------
          outlinedPrimary: {
            border: `1px solid ${Primary[600]}`,
            color: Primary[700],
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: Primary[50],
              borderColor: Primary[700],
            },
            "&.Mui-disabled": {
              borderColor: Primary[200],
              color: Primary[200],
            },
          },
          outlinedSecondary: {
            border: `1px solid ${Grey[300]}`,
            color: Grey[700],
            backgroundColor: Grey[100],
            "&:hover": { backgroundColor: Grey[200], borderColor: Grey[300] },
            "&.Mui-disabled": {
              color: Grey[300],
              backgroundColor: Grey[100],
              borderColor: Grey[200],
            },
          },
          outlinedError: {
            border: `1px solid ${Error[300]}`,
            color: Base.White,
            backgroundColor: Error[600],
            "&:hover": { backgroundColor: Error[700], borderColor: Error[700] },
            "&.Mui-disabled": {
              backgroundColor: Error[500],
              borderColor: Error[500],
            },
          },
          outlinedWarning: {
            border: `1px solid ${Warning[300]}`,
            color: Base.White,
            backgroundColor: Warning[600],
            "&:hover": {
              backgroundColor: Warning[700],
              borderColor: Warning[700],
            },
            "&.Mui-disabled": {
              backgroundColor: Warning[500],
              borderColor: Warning[500],
            },
          },
          outlinedSuccess: {
            border: `1px solid ${Success[300]}`,
            color: Base.White,
            backgroundColor: Success[600],
            "&:hover": {
              backgroundColor: Success[700],
              borderColor: Success[700],
            },
            "&.Mui-disabled": {
              backgroundColor: Success[500],
              borderColor: Success[500],
            },
          },

          // ------- TEXT -------
          textPrimary: {
            color: Primary[700],
            fontWeight: 700,
            padding: 0,
            "&:hover": { backgroundColor: "transparent" },
            "&:focus": { backgroundColor: "transparent" },
          },
          textSecondary: {
            color: Grey[700],
            padding: 0,
            "&:hover": { backgroundColor: "transparent" },
          },
          textError: {
            color: Error[700],
            padding: "10px 16px",
            "&:hover": { backgroundColor: "transparent" },
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: { backgroundColor: Grey[200], color: Grey[500] },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            color: Grey[500],
            ...text.xs.medium,
            ":focus": { color: Grey[500] },
            ":active": { color: Grey[500] },
            active: { color: Grey[500] },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h5: { color: Grey[900], ...display.sm.medium },
          h6: { color: Grey[700], ...text.sm.medium },
          subtitle2: {
            color: Grey[500],
            ...text.sm.normal,
            maxWidth: 200,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          subtitle1: { color: Grey[700], ...text.sm.normal },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: Grey[700],
            ...text.sm.normal,
            ":hover": { backgroundColor: Grey[50] },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { color: Grey[700], ...text.sm.normal } },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${Grey[100]}`,
            boxShadow:
              "0px 12px 16px -4px rgba(22, 31, 37, 0.08), 0px 4px 6px -2px rgba(22, 31, 37, 0.03)",
            borderRadius: 8,
          },
        },
      },
      MuiGrid: {
        styleOverrides: { item: { width: "100%" } },
      },
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: 'Play';
            font-style: normal;
            font-display: swap;
            font-weight: 400;
            src: url(${PlayFontRegular}) format('truetype');
          }
          @font-face {
            font-family: 'Play';
            font-style: normal;
            font-display: swap;
            font-weight: 700;
            src: url(${PlayFontBold}) format('truetype');
          }
          main { width: 100%; min-width: 320px; }
          .MuiButtonBase-root.MuiTableSortLabel-root.Mui-active { color: ${Grey[500]} }
          .MuiButtonBase-root.MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon { color: ${Grey[500]} }

          /* Custom scrollbar */
          ::-webkit-scrollbar { width: 8px; height: 8px; }
          ::-webkit-scrollbar-track { background: ${Grey[200]}; border-radius: 10px; }
          ::-webkit-scrollbar-thumb { background: ${Grey[500]}; border-radius: 10px; }
          ::-webkit-scrollbar-thumb:hover { background: ${Grey[600]}; }
          * { scrollbar-width: thin; scrollbar-color: ${Grey[500]} ${Grey[200]}; }
        `,
      },
    },
  },
  elGR
);
